// UniversalLearningEngine v1.0 - Orchestrateur Global
// Coordonne MasterParcoursEngine, PesaEngine, et futurs moteurs

import { MasterParcoursEngine } from './MasterParcoursEngineCore';
import { PesaEngine } from './PesaEngineCore';

const getUniversalStore = () => (typeof window !== 'undefined' ? window.__UNIVERSAL_LEARNING_STORE__ : null);

export class UniversalLearningEngine {
  constructor(learnerData, config = {}) {
    if (!learnerData?.userId) {
      throw new Error("UniversalEngine needs learnerData with userId");
    }

    this.userId = learnerData.userId;
    this.config = config;
    
    // State global
    this.state = {
      userId: learnerData.userId,
      totalPesas: learnerData.totalPesas || 0,
      globalProgress: learnerData.globalProgress || 0,
      certifications: learnerData.certifications || [],
      portfolio: learnerData.portfolio || {
        reflections: {},
        evidences: [],
        extractedConcepts: [],
        achievements: []
      },
      
      // Tracking des sous-moteurs actifs
      activeEngines: {
        parcours: null,   // Instance MasterParcoursEngine
        pesa: null,       // Instance PesaEngine
        capstone: null,   // [Futur]
        arena: null       // [Futur]
      },
      
      // Données de synchronisation
      engineStates: {
        parcours: null,
        pesa: null,
        capstone: null,
        arena: null
      },
      
      // Historique global
      activityLog: learnerData.activityLog || [],
      
      // Métadonnées
      lastSync: learnerData.lastSync || null,
      createdAt: learnerData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Chargement des états sauvegardés si disponibles
    if (learnerData.engineStates) {
      this.state.engineStates = learnerData.engineStates;
    }

    console.log("🚀 UniversalLearningEngine initialized for user:", this.userId);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PARCOURS ENGINE - Orchestration
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Initialise ou récupère l'instance MasterParcoursEngine
   */
  async initParcoursEngine(courseData, userProgress = null) {
    if (this.state.activeEngines.parcours) {
      console.warn("ParcoursEngine already active, returning existing instance");
      return this.state.activeEngines.parcours;
    }

    // Merge du portfolio global dans userProgress
    const enrichedProgress = {
      ...userProgress,
      harvestedData: {
        ...this.state.portfolio,
        ...(userProgress?.harvestedData || {})
      }
    };

    const engine = new MasterParcoursEngine(courseData, enrichedProgress);
    this.state.activeEngines.parcours = engine;

    // Synchronisation initiale
    this._syncFromParcoursEngine();

    this._log('engine_init', { 
      type: 'parcours', 
      courseId: courseData._id || courseData.id,
      courseName: courseData.nom || courseData.title 
    });

    return engine;
  }

  /**
   * Récupère l'engine parcours actif
   */
  getParcoursEngine() {
    return this.state.activeEngines.parcours;
  }

  /**
   * Synchronise les données du ParcoursEngine vers le state global
   */
  _syncFromParcoursEngine() {
    const engine = this.state.activeEngines.parcours;
    if (!engine) return;

    // 1. Agrégation des Pesas
    const parcoursContribution = engine.state.pesaCount || 0;
    
    // 2. Portfolio (récolte de données)
    if (engine.harvestedData) {
      this.state.portfolio = {
        ...this.state.portfolio,
        reflections: {
          ...this.state.portfolio.reflections,
          ...engine.harvestedData.reflections
        },
        extractedConcepts: [
          ...this.state.portfolio.extractedConcepts,
          ...(engine.harvestedData.extractedConcepts || [])
        ].filter((v, i, a) => a.indexOf(v) === i), // dedupe
        evidences: [
          ...this.state.portfolio.evidences,
          ...(engine.harvestedData.evidences || [])
        ]
      };
    }

    // 3. Sauvegarde de l'état sérialisé
    this.state.engineStates.parcours = engine.serialize();

    // 4. Certifications
    if (engine.state.isCertified) {
      this._addCertification({
        type: 'parcours',
        courseId: engine.course._id || engine.course.id,
        courseName: engine.course.nom || engine.course.title,
        completedAt: new Date().toISOString(),
        pesasEarned: parcoursContribution
      });
    }

    // 5. Recalcul des totaux
    this._recalculateGlobalStats();
    this._notifyStore();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PESA ENGINE - Orchestration
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Initialise ou récupère l'instance PesaEngine
   */
  async initPesaEngine(caseData, savedState = null) {
    if (this.state.activeEngines.pesa) {
      console.warn("PesaEngine already active, returning existing instance");
      return this.state.activeEngines.pesa;
    }

    const engine = new PesaEngine(caseData, {
      startTimer: true // active les timers automatiques
    });

    // Hydratation de l'état si disponible
    if (savedState) {
      engine.hydrate(savedState);
    }

    this.state.activeEngines.pesa = engine;

    // Synchronisation initiale
    this._syncFromPesaEngine();

    this._log('engine_init', { 
      type: 'pesa', 
      caseId: caseData._id || caseData.id,
      caseName: caseData.title || caseData.nom 
    });

    return engine;
  }

  /**
   * Récupère l'engine PESA actif
   */
  getPesaEngine() {
    return this.state.activeEngines.pesa;
  }

  /**
   * Synchronise les données du PesaEngine vers le state global
   */
  _syncFromPesaEngine() {
    const engine = this.state.activeEngines.pesa;
    if (!engine) return;

    // 1. Calcul du score PESA (basé sur emancipationScore ou autre métrique)
    const emancipationScore = this._calculateEmancipationScore(engine);
    const pesasEarned = Math.round(emancipationScore * 0.5); // 1 point émancipation = 0.5 Pesa

    // 2. Portfolio (features de calibration)
    if (engine.state.calibrationFeatures) {
      this.state.portfolio.achievements.push({
        type: 'pesa_case',
        caseId: engine.case._id || engine.case.id,
        features: engine.state.calibrationFeatures,
        score: emancipationScore,
        completedAt: new Date().toISOString()
      });
    }

    // 3. Sauvegarde de l'état sérialisé
    this.state.engineStates.pesa = engine.serialize();

    // 4. Certification si simulation terminée
    if (engine.isSimulationFinished()) {
      this._addCertification({
        type: 'pesa_case',
        caseId: engine.case._id || engine.case.id,
        caseName: engine.case.title || engine.case.nom,
        completedAt: new Date().toISOString(),
        pesasEarned: pesasEarned,
        emancipationScore: emancipationScore,
        solutionReached: engine.state.currentSolution?.name || 'hybrid'
      });
    }

    // 5. Recalcul des totaux
    this._recalculateGlobalStats();
    this._notifyStore();
  }

  /**
   * Calcul du score d'émancipation PESA
   * Peut être overridé selon la métrique utilisée dans le cas
   */
  _calculateEmancipationScore(pesaEngine) {
    if (!pesaEngine) return 0;

    // Si le cas définit sa propre méthode de calcul
    if (typeof pesaEngine.getEmancipationScore === 'function') {
      return pesaEngine.getEmancipationScore();
    }

    // Sinon, calcul générique basé sur les paramètres
    const params = pesaEngine.state.parameters || {};
    const weights = pesaEngine.case.metadata?.emancipationWeights || {};

    let score = 0;
    let totalWeight = 0;

    Object.entries(weights).forEach(([param, weight]) => {
      if (params[param] !== undefined) {
        score += params[param] * weight;
        totalWeight += weight;
      }
    });

    return totalWeight > 0 ? Math.round(score / totalWeight) : 50;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // GESTION GLOBALE
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Recalcule les statistiques globales
   */
  _recalculateGlobalStats() {
    // 1. Agrégation des Pesas
    let totalPesas = 0;

    if (this.state.engineStates.parcours) {
      totalPesas += this.state.engineStates.parcours.pesaCount || 0;
    }

    // Pesas des certifications PESA (déjà calculés)
    this.state.certifications
      .filter(cert => cert.type === 'pesa_case')
      .forEach(cert => {
        totalPesas += cert.pesasEarned || 0;
      });

    this.state.totalPesas = totalPesas;

    // 2. Progression globale (moyenne pondérée)
    let progressSum = 0;
    let progressWeight = 0;

    if (this.state.activeEngines.parcours) {
      const parcoursProgress = this.state.activeEngines.parcours.computeGlobalProgress();
      progressSum += parcoursProgress.percent * 0.7; // 70% du poids
      progressWeight += 0.7;
    }

    if (this.state.activeEngines.pesa && this.state.activeEngines.pesa.isSimulationFinished()) {
      progressSum += 100 * 0.3; // 30% du poids
      progressWeight += 0.3;
    }

    this.state.globalProgress = progressWeight > 0 
      ? Math.round(progressSum / progressWeight) 
      : 0;

    this.state.updatedAt = new Date().toISOString();
  }

  /**
   * Ajoute une certification (évite les doublons)
   */
  _addCertification(cert) {
    const exists = this.state.certifications.some(c => 
      c.type === cert.type && 
      (c.courseId === cert.courseId || c.caseId === cert.caseId)
    );

    if (!exists) {
      this.state.certifications.push(cert);
      this._log('certification_earned', cert);
    }
  }

  /**
   * Ajoute une entrée au journal d'activité
   */
  _log(type, data) {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: new Date().toISOString()
    };

    this.state.activityLog.push(entry);

    // Limite à 1000 entrées max
    if (this.state.activityLog.length > 1000) {
      this.state.activityLog = this.state.activityLog.slice(-1000);
    }
  }

  /**
   * Notification du store global (si disponible)
   */
  _notifyStore() {
    const store = getUniversalStore();
    if (!store) return;

    store.setState({
      userId: this.userId,
      totalPesas: this.state.totalPesas,
      globalProgress: this.state.globalProgress,
      certifications: [...this.state.certifications],
      portfolio: { ...this.state.portfolio },
      activityLog: [...this.state.activityLog],
      updatedAt: this.state.updatedAt
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SYNCHRONISATION & PERSISTANCE
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Sérialise l'état complet pour sauvegarde MongoDB
   */
  serialize() {
    return {
      userId: this.userId,
      totalPesas: this.state.totalPesas,
      globalProgress: this.state.globalProgress,
      certifications: this.state.certifications,
      portfolio: this.state.portfolio,
      
      // États des moteurs individuels
      engineStates: {
        parcours: this.state.activeEngines.parcours 
          ? this.state.activeEngines.parcours.serialize() 
          : this.state.engineStates.parcours,
        pesa: this.state.activeEngines.pesa 
          ? this.state.activeEngines.pesa.serialize() 
          : this.state.engineStates.pesa,
      },
      
      // Métadonnées
      activityLog: this.state.activityLog.slice(-100), // Sauvegarder les 100 dernières activités
      lastSync: new Date().toISOString(),
      createdAt: this.state.createdAt,
      updatedAt: this.state.updatedAt
    };
  }

  /**
   * Hydrate depuis un état sauvegardé
   */
  hydrate(savedState) {
    if (!savedState) return;

    Object.assign(this.state, {
      totalPesas: savedState.totalPesas || 0,
      globalProgress: savedState.globalProgress || 0,
      certifications: savedState.certifications || [],
      portfolio: savedState.portfolio || this.state.portfolio,
      engineStates: savedState.engineStates || {},
      activityLog: savedState.activityLog || [],
      lastSync: savedState.lastSync,
      createdAt: savedState.createdAt || this.state.createdAt,
      updatedAt: savedState.updatedAt || new Date().toISOString()
    });
  }

  /**
   * Sauvegarde vers MongoDB (à appeler périodiquement ou sur événements critiques)
   */
  async saveToBackend(apiUrl = process.env.REACT_APP_API_URL) {
    const payload = this.serialize();

    try {
      const response = await fetch(`${apiUrl}/api/learners/${this.userId}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Save failed');

      this.state.lastSync = new Date().toISOString();
      this._log('backend_sync', { status: 'success' });
      
      return { success: true };
    } catch (error) {
      console.error('Backend save failed:', error);
      this._log('backend_sync', { status: 'failed', error: error.message });
      return { success: false, error };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HOOKS DE SYNCHRONISATION INTER-MOTEURS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Hook appelé quand un granule est validé dans MasterParcours
   */
  onParcoursGranuleValidated(granuleId, results) {
    const engine = this.state.activeEngines.parcours;
    if (!engine) return;

    // Validation dans l'engine parcours
    engine.validateGranule(granuleId, results);

    // Sync globale
    this._syncFromParcoursEngine();

    // Log
    this._log('granule_validated', {
      granuleId,
      pesaGain: results.pesaGain || 0,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Hook appelé quand une action est appliquée dans PESA
   */
  onPesaActionApplied(actionId, result) {
    const engine = this.state.activeEngines.pesa;
    if (!engine) return;

    // L'action a déjà été appliquée dans l'engine PESA
    // On se contente de synchroniser
    this._syncFromPesaEngine();

    // Log
    this._log('pesa_action', {
      actionId,
      success: result.success,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Hook appelé quand un module/step est complété
   */
  onModuleCompleted(moduleId, engineType) {
    this._log('module_completed', {
      moduleId,
      engineType,
      timestamp: new Date().toISOString()
    });

    // Vérification de déblocage conditionnel
    // Ex: "Débloquer PESA Case X si Module Y complété"
    this._checkConditionalUnlocks();
  }

  /**
   * Vérifie et applique les déblocages conditionnels entre moteurs
   */
  _checkConditionalUnlocks() {
    // Exemple : Débloquer une simulation PESA si parcours à 50%
    if (this.state.globalProgress >= 50) {
      // Logique de déblocage custom
      this._log('conditional_unlock', {
        condition: 'global_progress >= 50%',
        unlocked: 'advanced_pesa_cases'
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // UTILITAIRES & GETTERS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Récupère les stats globales
   */
  getGlobalStats() {
    return {
      userId: this.userId,
      totalPesas: this.state.totalPesas,
      globalProgress: this.state.globalProgress,
      certificationsCount: this.state.certifications.length,
      portfolioSize: {
        reflections: Object.keys(this.state.portfolio.reflections).length,
        evidences: this.state.portfolio.evidences.length,
        achievements: this.state.portfolio.achievements.length
      },
      lastSync: this.state.lastSync,
      updatedAt: this.state.updatedAt
    };
  }

  /**
   * Récupère le portfolio complet
   */
  getPortfolio() {
    return { ...this.state.portfolio };
  }

  /**
   * Récupère l'historique d'activité
   */
  getActivityLog(limit = 50) {
    return this.state.activityLog.slice(-limit);
  }

  /**
   * Vérifie si l'apprenant peut soumettre au mentor
   */
  canSubmitToMentor() {
    // Critères combinés
    const parcoursEngine = this.state.activeEngines.parcours;
    const hasSufficientPortfolio = 
      Object.keys(this.state.portfolio.reflections).length >= 5 &&
      this.state.portfolio.evidences.length >= 3;

    const parcoursReady = parcoursEngine 
      ? parcoursEngine.canSubmitToMentor() 
      : false;

    return hasSufficientPortfolio && parcoursReady;
  }

  /**
   * Réinitialise un moteur spécifique
   */
  resetEngine(engineType) {
    const engine = this.state.activeEngines[engineType];
    if (!engine) return false;

    if (typeof engine.reset === 'function') {
      engine.reset();
      this.state.engineStates[engineType] = null;
      this._log('engine_reset', { engineType });
      return true;
    }

    return false;
  }

  /**
   * Détruit proprement l'instance
   */
  destroy() {
    // Cleanup des timers PESA
    if (this.state.activeEngines.pesa) {
      const pesaEngine = this.state.activeEngines.pesa;
      if (pesaEngine.phaseTimer) {
        clearTimeout(pesaEngine.phaseTimer);
      }
      pesaEngine._timers?.forEach(t => clearTimeout(t));
    }

    // Clear references
    this.state.activeEngines = {
      parcours: null,
      pesa: null,
      capstone: null,
      arena: null
    };

    console.log("🔥 UniversalLearningEngine destroyed");
  }
}